import React, { useState, useEffect } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { motion } from 'framer-motion';
import { Clock, Hash, FileText, CheckCircle2, ChevronRight, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import EvidenceChain from '@/components/apex/EvidenceChain';
import ReconstructionPacketDrawer from '@/components/apex/ReconstructionPacketDrawer';
import ProofStatusBadge from '@/components/apex/ProofStatusBadge';
import ProvenanceDepthBar from '@/components/apex/ProvenanceDepthBar';
import RecordDrawer from '@/components/apex/RecordDrawer';
import { toast } from 'sonner';

const STAGES = [
  'Reading CloudTrail history...',
  'Computing active control state...',
  'Building identity permissions...',
  'Mapping exposure windows...',
  'Generating evidence chain...',
];

const PRESETS = [
  { label: 'At last gap start', date: '2026-04-12' },
  { label: 'Before critical change', date: '2025-11-27' },
  { label: 'At audit date · Mar 15', date: '2026-03-15' },
  { label: 'During incident · Apr 14', date: '2026-04-14' },
  { label: '24 hours ago', date: '2026-04-22' },
];

// Proof obligations rendered at the canonical reconstruction date.
// Each row maps to a control in the customer's framework baseline. The three breach-linked
// obligations carry an investigationId so their drawer can pivot directly into INV-2026-042.
const OBLIGATIONS = [
  {
    framework: 'PCI DSS', control: '3.4',
    description: 'Render PAN unreadable anywhere it is stored',
    obligation: 'Encryption at Rest evidence for all PCI-scoped storage',
    met: false,
    reason: 'Config rule inactive 9 days · No DisableConfigRule in CloudTrail',
    proof: 'PROVEN', depth: 9.1,
    investigationId: 'INV-2026-042',
    evidenceIds: ['evt-2026-04-12-config-gap-start', 'evt-2026-04-21-missing-config'],
    sources: ['CloudTrail', 'AWS Config'],
    impact: 'PCI-scoped storage operated without continuous encryption attestation for 9 days. Auditor will mark this as a material control failure.',
    debtUsd: 89000,
    accounts: ['stratum-payments-prod', 'stratum-banking-prod'],
  },
  {
    framework: 'PCI DSS', control: '10.1',
    description: 'Implement audit trails for all access to system components',
    obligation: 'CloudTrail org logging continuously enabled across all 47 accounts',
    met: true,
    reason: '0 disable events · hash chain intact',
    proof: 'PROVEN', depth: 9.8,
    evidenceIds: ['EVD-CT001-20260421'],
    sources: ['CloudTrail', 'AWS Organizations'],
    impact: 'Logging coverage is continuous across all 47 accounts. Hash chain over the period is unbroken.',
    debtUsd: 0,
    accounts: ['ALL'],
  },
  {
    framework: 'SOC 2', control: 'CC6.3',
    description: 'Logical and physical access controls',
    obligation: 'No dev identities may authenticate into production accounts',
    met: false,
    reason: 'developer-console-access executed AssumeRole into stratum-payments-prod',
    proof: 'PROVEN', depth: 9.4,
    investigationId: 'INV-2026-042',
    evidenceIds: ['evt-2026-04-14-assumerole', 'evt-2025-10-03-iam-trust'],
    sources: ['CloudTrail', 'IAM Access Analyzer'],
    impact: 'Cross-environment trust path was traversed at 02:17 UTC, leading to PCI data access. Direct violation of access control boundaries.',
    debtUsd: 127000,
    accounts: ['stratum-dev-sandbox', 'stratum-payments-prod'],
  },
  {
    framework: 'SOC 2', control: 'CC8.1',
    description: 'Change management',
    obligation: 'Production deployments must originate from approved pipeline identity',
    met: true,
    reason: 'No untracked deployments outside CHG-* tickets',
    proof: 'PROVEN', depth: 8.6,
    evidenceIds: ['EVD-CT005-20260421'],
    sources: ['CloudTrail', 'GitHub Actions'],
    impact: 'All production deployments map back to approved CHG tickets and pipeline identities.',
    debtUsd: 0,
    accounts: ['ALL_PROD'],
  },
  {
    framework: 'ISO 27001', control: 'A.12.4',
    description: 'Logging and monitoring',
    obligation: 'Privileged-action audit trail for all key-management operations',
    met: false,
    reason: 'CloudTrail data events not enabled for KMS in stratum-dev-sandbox',
    proof: 'UNVERIFIABLE', depth: 2.1,
    reasonCode: 'MISSING_CLOUDTRAIL_DATA_EVENT',
    evidenceIds: [],
    sources: ['CloudTrail (data events disabled)'],
    impact: 'Cannot prove or disprove key-management compliance in stratum-dev-sandbox during the period. Honest gap; not a finding.',
    debtUsd: 14000,
    accounts: ['stratum-dev-sandbox'],
  },
  {
    framework: 'DORA', control: 'Art. 9',
    description: 'ICT security policies',
    obligation: 'Encryption keys must only be decrypted by approved identities',
    met: true,
    reason: 'No KMS Decrypt by out-of-policy principals',
    proof: 'INFERRED', depth: 6.2,
    evidenceIds: ['EVD-CT006-20260421'],
    sources: ['CloudTrail (mgmt events)', 'KMS Key Policy'],
    impact: 'Inferred from management events — data-event coverage gap means we infer rather than directly observe.',
    debtUsd: 0,
    accounts: ['ALL_PROD'],
  },
  {
    framework: 'DORA', control: 'Art. 25',
    description: 'Continuous threat-led testing',
    obligation: 'Crown-jewel accounts must retain telemetry coverage',
    met: false,
    reason: 'Config evaluations absent for 9 days in stratum-payments-prod',
    proof: 'PROVEN', depth: 8.9,
    investigationId: 'INV-2026-042',
    evidenceIds: ['EVD-CT004-20260412'],
    sources: ['AWS Config', 'CloudTrail'],
    impact: 'Telemetry gap on crown-jewel account during the breach window. Auditor reportable under DORA.',
    debtUsd: 41000,
    accounts: ['stratum-payments-prod'],
  },
];

export default function Reconstruction() {
  const { seed, API } = useApp();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryDate = params.get('date');
  const [account, setAccount] = useState('stratum-payments-prod');
  const [date, setDate] = useState(queryDate || '2026-03-15');
  const [time, setTime] = useState('14:32');
  const [running, setRunning] = useState(false);
  const [completed, setCompleted] = useState([]);
  const [hasResult, setHasResult] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);
  const [activeObligation, setActiveObligation] = useState(null);

  // If query string changes, sync the date
  useEffect(() => {
    if (queryDate) setDate(queryDate);
  }, [queryDate]);

  const run = async () => {
    setRunning(true); setCompleted([]); setHasResult(false);
    for (let i = 0; i < STAGES.length; i++) {
      await new Promise((r) => setTimeout(r, 380));
      setCompleted((c) => [...c, i]);
    }
    setRunning(false); setHasResult(true);
  };

  if (!seed) return null;

  return (
    <div className="space-y-5" data-testid="reconstruction">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Forensics · Regulatory Time Travel</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Evidence Reconstruction</h1>
        <p className="text-sm text-apex-neutral mt-1 max-w-2xl">
          Reconstruct the exact governance state of any account at any point in time. Every finding traces to an immutable CloudTrail record.
        </p>
      </div>

      <Tabs defaultValue="controls" className="lg:hidden">
        <TabsList className="w-full">
          <TabsTrigger value="controls" className="flex-1">Controls</TabsTrigger>
          <TabsTrigger value="results" className="flex-1">Results</TabsTrigger>
        </TabsList>
        <TabsContent value="controls"><Controls {...{ seed, account, setAccount, date, setDate, time, setTime, run, running }} variant="mobile" /></TabsContent>
        <TabsContent value="results"><Results {...{ running, completed, hasResult, account, date, time, openPacket: () => setPacketOpen(true), openObligation: setActiveObligation }} /></TabsContent>
      </Tabs>

      <div className="hidden lg:grid grid-cols-[420px,1fr] gap-5">
        <Controls {...{ seed, account, setAccount, date, setDate, time, setTime, run, running }} variant="desktop" />
        <Results {...{ running, completed, hasResult, account, date, time, openPacket: () => setPacketOpen(true), openObligation: setActiveObligation }} />
      </div>

      <ReconstructionPacketDrawer
        open={packetOpen}
        onOpenChange={setPacketOpen}
        account={account}
        date={date}
        time={time}
        API={API}
      />

      <RecordDrawer
        open={!!activeObligation}
        onOpenChange={(o) => !o && setActiveObligation(null)}
        record={activeObligation ? buildObligationRecord(activeObligation, navigate, date) : null}
        testId="recon-obligation-drawer"
      />
    </div>
  );
}

function Controls({ seed, account, setAccount, date, setDate, time, setTime, run, running, variant = 'desktop' }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
      <div>
        <label className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">Account</label>
        <select value={account} onChange={(e) => setAccount(e.target.value)} data-testid="recon-account" className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:border-apex-lens font-mono">
          {seed.key_accounts.map((a) => <option key={a.name}>{a.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">Date</label>
          <Input data-testid={`recon-date-${variant}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 font-mono" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">Time UTC</label>
          <Input data-testid="recon-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1 font-mono" />
        </div>
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold">Comparison target</label>
        <select className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white">
          <option>Current state</option>
          <option>Approved baseline</option>
          <option>Custom date</option>
        </select>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold mb-2">Presets</div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button key={p.label} onClick={() => setDate(p.date)} className="text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 text-apex-ink-2">
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <Button onClick={run} disabled={running} data-testid={variant === 'mobile' ? 'recon-run-mobile' : 'recon-run'} className="w-full bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5">
        {running ? <><Loader2 className="w-4 h-4 animate-spin" />Reconstructing…</> : <><Clock className="w-4 h-4" />Reconstruct Governance State</>}
      </Button>

      <div className="text-[11px] text-apex-neutral">
        Coverage note: VPC Flow Logs not enabled in stratum-dev-sandbox — network activity in that account has 67% coverage during selected period.
      </div>
    </div>
  );
}

function Results({ running, completed, hasResult, account, date, time, openPacket, openObligation }) {
  return (
    <div className="bg-apex-shell text-slate-200 rounded-xl border border-slate-800 p-5 font-mono text-xs leading-relaxed min-h-[480px] grain">
      {!running && !hasResult && (
        <div className="text-slate-500 h-full flex items-center justify-center text-center px-6 py-20">
          Configure a moment in time on the left, then click <span className="text-apex-lens mx-1">Reconstruct</span> to view the exact governance state.
        </div>
      )}

      {(running || hasResult) && (
        <div className="space-y-1">
          {STAGES.map((s, i) => {
            const done = completed.includes(i);
            return (
              <div key={i} className="flex items-center gap-2">
                {done ? <CheckCircle2 className="w-3.5 h-3.5 text-apex-lens" /> : <Loader2 className={`w-3.5 h-3.5 ${i === completed.length ? 'animate-spin text-apex-lens' : 'text-slate-600'}`} />}
                <span className={done ? 'text-slate-200' : i === completed.length ? 'text-slate-200' : 'text-slate-600'}>{s}</span>
                {done && <span className="text-apex-lens ml-auto">complete</span>}
              </div>
            );
          })}
        </div>
      )}

      {hasResult && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Governance State</div>
            <div className="text-base text-white font-bold">{account}</div>
            <div className="text-slate-400">{date} · {time}:00 UTC</div>
            <div className="mt-1 text-[10px] text-apex-lens">Confidence: 98.7% event coverage · 127,847 CloudTrail events analyzed</div>
            <div className="mt-1 text-[10px] text-apex-neutral font-mono" data-testid="recon-proof-summary">
              Proof status: <span className="text-teal-400">4 PROVEN</span> · <span className="text-amber-400">1 INFERRED</span> · <span className="text-red-400">1 UNVERIFIABLE</span>
            </div>
            <div className="text-[10px] text-slate-500 truncate">Evidence ID: EVD-{date}-reconstruction · sha256:b4e7c2d1f3a5b7c9e1d3f5a7b9c1e3f5</div>
          </div>

          <Section title="Active Controls — Proof Status">
            {[
              { label: 'SCP: Deny S3 Public Access',         state: 'ENFORCED',                     proof: 'PROVEN',       depth: 9.2, sources: ['CloudTrail', 'Config'] },
              { label: 'Config: Encryption at Rest',          state: 'GAP — Day 4 of 11-day gap',    proof: 'PROVEN',       depth: 9.1, sources: ['CloudTrail', 'Config'], warn: true },
              { label: 'GuardDuty: Threat Detection',         state: 'ENFORCED',                     proof: 'PROVEN',       depth: 8.4, sources: ['CloudTrail', 'GuardDuty'] },
              { label: 'Control Tower: Baseline Guardrails',  state: 'ENFORCED',                     proof: 'INFERRED',     depth: 6.8, sources: ['Config', 'CloudTrail'] },
              { label: 'PCI: Network Segmentation',           state: 'GAP — Day 2 of 6-day gap',     proof: 'PROVEN',       depth: 8.7, sources: ['VPC Flow', 'Config'], warn: true },
              { label: 'KMS: Key Usage Audit',                state: 'ENFORCED',                     proof: 'UNVERIFIABLE', depth: 2.1, sources: ['CloudTrail'], reasonCode: 'MISSING_CLOUDTRAIL_DATA_EVENT' },
            ].map((ctrl) => (
              <div
                key={ctrl.label}
                data-testid={`recon-ctrl-${ctrl.label.split(':')[0].toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-start gap-2 py-1 border-b border-slate-800/40 last:border-0 ${ctrl.warn ? 'text-apex-warning' : 'text-slate-200'}`}
              >
                <span className="shrink-0 mt-0.5">{ctrl.warn ? '⚠' : '✓'}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-[11px]">{ctrl.label} — {ctrl.state}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <ProofStatusBadge status={ctrl.proof} reasonCode={ctrl.reasonCode ?? null} size="xs" />
                  <ProvenanceDepthBar depth={ctrl.depth} size="xs" />
                </div>
              </div>
            ))}
          </Section>

          <Section title="Active Identity Permissions (excerpt)">
            <Row warn>lambda-payments-processor — AdministratorAccess (granted 847 actions)</Row>
            <Row>terraform-prod-role — 312 granted, 287 used</Row>
            <Row>github-actions-deploy-role — 42 granted, 38 used</Row>
          </Section>

          <Section title="Active Exposure Windows">
            <Row warn>Config: Encryption at Rest — 3 events during gap (S3 PutBucketEncryption disabled, RDS unencrypted snapshot)</Row>
            <Row warn>PCI: Network Segmentation — 4 events during gap (sg modify, peering attach)</Row>
          </Section>

          <Section title="Activity During Gap (governance-relevant)">
            <Row>02:14 UTC iam:CreatePolicyVersion · developer-console-access</Row>
            <Row>02:17 UTC sts:AssumeRole · stratum-dev-sandbox → stratum-payments-prod</Row>
            <Row>02:18 UTC s3:GetObject · stratum-pci-customer-data ×3</Row>
          </Section>

          <Section title="Proof Obligations — Framework Controls at this Date">
            {OBLIGATIONS.map((o, i) => {
              const slug = `${o.framework.toLowerCase().replace(/\s+/g, '-')}-${o.control.toLowerCase().replace(/[\s.]+/g, '-')}`;
              return (
                <button
                  key={i}
                  type="button"
                  data-testid={`recon-obligation-${slug}`}
                  onClick={() => openObligation?.(o)}
                  className={`w-full flex items-start gap-2 py-1.5 text-left border-b border-slate-800/40 last:border-0 hover:bg-slate-800/40 rounded-sm px-1 transition-colors ${o.met ? 'text-slate-200' : 'text-apex-warning'}`}
                >
                  <span className={`shrink-0 mt-0.5 font-bold ${o.met ? 'text-emerald-400' : 'text-red-400'}`}>{o.met ? '✓' : '✗'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[10px] font-mono text-apex-lens">{o.framework} {o.control}</span>
                      <span className="text-[10px] text-slate-400 truncate">— {o.description}</span>
                      {o.investigationId && (
                        <span className="text-[9px] font-mono text-rose-300 bg-rose-900/40 border border-rose-800/60 rounded px-1 py-px">
                          {o.investigationId}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] mt-0.5">{o.obligation}</div>
                    <div className={`text-[10px] mt-0.5 font-mono ${o.met ? 'text-slate-500' : 'text-amber-300'}`}>{o.met ? 'Met: ' : 'Unmet: '}{o.reason}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <ProofStatusBadge status={o.proof} reasonCode={o.reasonCode ?? null} size="xs" displayOnly />
                    <ProvenanceDepthBar depth={o.depth} size="xs" />
                    <ChevronRight className="w-3 h-3 text-slate-500 mt-0.5" />
                  </div>
                </button>
              );
            })}
            <div className="text-[9px] font-mono text-slate-500 pt-2 mt-1 border-t border-slate-800/60">
              At this reconstruction date: <span className="text-emerald-400">3 obligations met</span> · <span className="text-red-400">4 unmet</span> · Each obligation maps to a control in your framework baseline. Unmet obligations accrue governance debt. <span className="text-slate-400">Click any row to inspect the full evidence chain.</span>
            </div>
          </Section>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-800">
            <Button onClick={openPacket} data-testid="recon-generate-packet" className="bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5"><FileText className="w-3.5 h-3.5" />Generate Signed Evidence Packet</Button>
            <Button
              onClick={async () => {
                const id = `EVD-${date}-reconstruction`;
                try { await navigator.clipboard.writeText(id); toast.success('Evidence ID copied', { description: id }); }
                catch { toast.error('Copy failed'); }
              }}
              data-testid="recon-copy-id"
              variant="outline"
              className="border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white bg-transparent gap-1.5"
            >
              <Hash className="w-3.5 h-3.5" />Copy Evidence ID
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">{title}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}
function Row({ children, ok, warn }) {
  const color = ok ? 'text-emerald-400' : warn ? 'text-amber-400' : 'text-slate-300';
  const marker = ok ? '✓' : warn ? '!' : '·';
  return <div className={`flex gap-2 ${color}`}><span className="w-3 shrink-0">{marker}</span><span className="flex-1">{children}</span></div>;
}

// Build a RecordDrawer schema for a single proof obligation. Includes provenance evidence,
// affected accounts, governance impact, and (when relevant) a pivot into the linked investigation.
function buildObligationRecord(o, navigate, reconDate) {
  const slug = `${o.framework.toLowerCase().replace(/\s+/g, '-')}-${o.control.toLowerCase().replace(/[\s.]+/g, '-')}`;
  const isUnverifiable = o.proof === 'UNVERIFIABLE';
  const tone = o.met ? 'positive' : (isUnverifiable ? 'neutral' : 'critical');
  const severity = o.met ? 'low' : (isUnverifiable ? 'medium' : 'high');
  const debtK = (o.debtUsd / 1000).toFixed(0);

  const sections = [
    {
      title: 'Provenance',
      type: 'kv',
      items: [
        { label: 'Proof status', value: o.proof, mono: true },
        { label: 'Provenance depth', value: `${o.depth.toFixed(1)} / 10`, mono: true },
        { label: 'Sources', value: o.sources.join(' + '), mono: true },
        { label: 'Reconstructed at', value: reconDate, mono: true },
      ],
    },
    {
      title: 'Evidence chain',
      type: 'list',
      items: o.evidenceIds.length
        ? o.evidenceIds
        : ['No evidence available — source telemetry was not enabled during this period.'],
      mono: true,
    },
    {
      title: 'Governance impact',
      type: 'kpis',
      kpis: [
        {
          label: 'Status at this date',
          value: o.met ? 'Met' : 'Unmet',
          sub: o.met ? 'Within baseline' : 'Counts toward governance debt',
          tone: o.met ? 'success' : 'warning',
          delta: o.met ? 'flat' : 'up',
        },
        {
          label: 'Debt accrued',
          value: o.debtUsd > 0 ? `$${debtK}K` : '$0',
          sub: o.debtUsd > 0 ? 'Quarterly run-rate' : 'No accrual at this date',
          tone: o.debtUsd > 0 ? 'warning' : 'success',
          delta: o.debtUsd > 0 ? 'up' : 'flat',
        },
        {
          label: 'Auditor signal',
          value: o.met ? 'Routine' : (isUnverifiable ? 'Disclosable gap' : 'Material'),
          sub: o.met ? 'Standard quarterly evidence' : (isUnverifiable ? 'Honest coverage gap, not a finding' : 'Surfaces in next audit pull'),
          tone: o.met ? 'success' : (isUnverifiable ? 'lens' : 'critical'),
          delta: o.met ? 'flat' : 'up',
        },
      ],
    },
    {
      title: 'Affected accounts',
      type: 'list',
      items: o.accounts,
      mono: true,
    },
    {
      title: 'Reasoning',
      type: 'callout',
      callout: { tone: o.met ? 'positive' : 'warning', body: o.impact },
    },
  ];

  const actions = [];
  if (o.investigationId) {
    actions.push({
      label: `Open ${o.investigationId}`,
      primary: true,
      onClick: () => navigate(`/lens/investigations?id=${o.investigationId}`),
      testId: `recon-obligation-${slug}-inv-link`,
    });
  }
  actions.push({
    label: 'Open Reconstruction packet',
    onClick: () => navigate('/lens/reconstruction'),
    testId: `recon-obligation-${slug}-packet`,
  });

  return {
    idTag: `${o.framework} ${o.control}`,
    title: o.obligation,
    subtitle: o.description,
    severity,
    verdict: {
      tone,
      label: o.met
        ? 'Obligation met'
        : (isUnverifiable ? 'Obligation unverifiable' : 'Obligation unmet'),
      sub: o.met
        ? 'At the reconstructed date, evidence supports this obligation.'
        : (isUnverifiable
            ? 'Source telemetry was not enabled. We can neither prove nor disprove compliance.'
            : 'At the reconstructed date, evidence contradicts this obligation. Governance debt is accruing.'),
    },
    signal: {
      tone,
      lead: o.met ? 'Evidence:' : 'Why this is unmet:',
      body: o.reason,
    },
    meta: [
      { label: 'Framework', value: `${o.framework} ${o.control}`, mono: true },
      { label: 'Proof status', value: o.proof, mono: true, tone: o.met ? 'success' : (isUnverifiable ? 'neutral' : 'warning') },
      { label: 'Depth', value: `${o.depth.toFixed(1)} / 10`, mono: true },
      { label: 'Linked investigation', value: o.investigationId ?? '—', mono: true, tone: o.investigationId ? 'warning' : undefined },
    ],
    sections,
    actions,
  };
}
